// src/lib/analytics.ts
//
// The one file that knows Mixpanel exists. Everything else calls `track`
// with a name from EVENTS, so the tool can be swapped here and nowhere else.
//
// Off without a token and in dev by default — local testing is already a
// third of the database's activity; it should not be a third of the
// analytics too. Set VITE_ANALYTICS_DEV=1 to opt in locally.

import mixpanel from "mixpanel-browser";

const TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN as string | undefined;
const ENABLED =
  !!TOKEN && (import.meta.env.PROD || import.meta.env.VITE_ANALYTICS_DEV === "1");

let ready = false;

function init() {
  if (ready || !ENABLED) return;
  mixpanel.init(TOKEN!, {
    api_host: `${window.location.origin}/mp`,
    // Tracks page views on SPA route changes (history API), not just load.
    track_pageview: "url-with-path",
    persistence: "localStorage",
    ignore_dnt: true,
    debug: import.meta.env.DEV,
  });
  ready = true;
}

init();

/**
 * Deliberately small. Each event answers one question the DB cannot:
 * the first-action drop, mock completion, the paywall funnel.
 */
export const EVENTS = {
  SIGNED_IN: "signed_in",
  ONBOARDING_COMPLETED: "onboarding_completed",
  MOCK_STARTED: "mock_started",
  PRACTICE_STARTED: "practice_started",
  ATTEMPT_COMPLETED: "attempt_completed", // props: kind, total, answered, correct
  DAILY_ANSWERED: "daily_answered",
  STUDY_SESSION_SAVED: "study_session_saved",
  CONTEST_CREATED: "contest_created",
  // Paywall funnel — wired now so the pricing page lights up the day it ships.
  SOLUTION_LOCKED_SEEN: "solution_locked_seen",
  PASS_CTA_CLICKED: "pass_cta_clicked",
  CHECKOUT_STARTED: "checkout_started",
  PASS_PURCHASED: "pass_purchased",
} as const;

export type EventName = (typeof EVENTS)[keyof typeof EVENTS];

export function track(event: EventName, props?: Record<string, unknown>) {
  if (!ENABLED) return;
  init();
  try {
    mixpanel.track(event, props);
  } catch {
    /* analytics must never break the app */
  }
}

/** Call once per login. `plan` and `branch` let every report split by them. */
export function identify(
  userId: string,
  profile: { email?: string; name?: string; branch?: string | null; plan?: "free" | "pass" },
) {
  if (!ENABLED) return;
  init();
  try {
    mixpanel.identify(userId);
    mixpanel.people.set({
      $email: profile.email,
      $name: profile.name,
      branch: profile.branch ?? undefined,
      plan: profile.plan ?? "free",
    });
    mixpanel.people.set_once({ first_seen: new Date().toISOString() });
  } catch {
    /* ignore */
  }
}

/** Update a profile property without re-identifying (e.g. after onboarding). */
export function setProfile(props: Record<string, unknown>) {
  if (!ENABLED || !ready) return;
  try {
    mixpanel.people.set(props);
  } catch {
    /* ignore */
  }
}

/** On sign-out, so the next login on this device is not stitched to this user. */
export function reset() {
  if (!ENABLED || !ready) return;
  try {
    mixpanel.reset();
  } catch {
    /* ignore */
  }
}
